import { Trans, useLingui } from "@lingui/react/macro";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import Form from "@/components/Form/Form";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import {
  ANY_TIME_OF_DAY,
  addActivity,
  type INewActivity,
} from "@/api/Activities";
import { buildNewActivity } from "./buildNewActivity";
import { deducedError } from "@/Utils/ErrorUtils";
import type {
  IActivityCategory,
  IMeasurementsType,
  IVideoType,
} from "@medoma/models/activities";
import { startOfToday } from "date-fns";
import {
  categorySchema,
  getActivityCategoryConfig,
  measurementSchema,
  timeOfDaySchema,
} from "@medoma/models/activities";
import {
  calendarDateSchema,
  dateInputSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import type { IEmployeeCompetence } from "@medoma/models/shifts";
import { defaultRequiredCompetences } from "@medoma/models/activityRequirements";
import {
  TitleInput,
  type ITitleInputFields,
} from "@/forms/AddActivity/TitleInput";
import {
  DescriptionInput,
  type IDescriptionInputFields,
} from "@/forms/AddActivity/DescriptionInput";
import {
  CompetencePicker,
  type ICompetencePickerFields,
} from "@/forms/AddActivity/CompetencePicker";
import {
  ActivityDuration,
  type IActivityDurationFields,
} from "@/forms/AddActivity/ActivityDuration";
import { DEFAULT_ACTIVITY_DURATION_OPTION } from "@/forms/AddActivity/activityDurationModels";
import { convertDurationToMinutes } from "@/forms/AddActivity/activityDurationUtils";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  Scheduling,
  type ISchedulingFields,
} from "@/forms/AddActivity/Scheduling/Scheduling";
import {
  activityFrequencySchema,
  customRecurrenceIntervalUnitSchema,
  recurringOptionSchema,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import {
  getTimeFields,
  getTimespan,
} from "@/forms/AddActivity/activityTimeUtils";
import {
  ActivityCategoryPicker,
  type IActivityCategoryPickerFields,
} from "@/forms/AddActivity/ActivityCategoryPicker";
import { MeasurementsPicker } from "@/forms/AddActivity/MeasurementsPicker";

export type IAddActivityFormData = ITitleInputFields &
  IDescriptionInputFields &
  ISchedulingFields &
  ICompetencePickerFields &
  IActivityDurationFields &
  IActivityCategoryPickerFields & {
    doubleStaffing: boolean;
    hidden: boolean;
    type: IVideoType;
    measurements: IMeasurementsType[];
  };

export const AddActivityForm = ({
  onSuccess,
  currentEmployeeCompetence,
}: {
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { t } = useLingui();
  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const methods = useForm<IAddActivityFormData>({
    defaultValues: {
      category: categorySchema.enum.HomeVisit,
      title: "",
      description: "",
      hidden: false,
      doubleStaffing: false,
      recurrencesPerDay: "1",
      customRecurrenceIntervalUnit:
        customRecurrenceIntervalUnitSchema.enum.weeks,
      customRecurrenceInterval: undefined,
      durationOption: DEFAULT_ACTIVITY_DURATION_OPTION,
      customDuration: undefined,
      customDurationUnit: undefined,
      timeCategory: timeOfDaySchema.enum.Any,
      timeslots: undefined,
      weekdays: undefined,
      recurrence: undefined,
      frequency: activityFrequencySchema.enum.oneTime,
      requiredCompetences: defaultRequiredCompetences(
        currentEmployeeCompetence,
      ),
      measurements: [
        measurementSchema.enum.bloodPressure,
        measurementSchema.enum.pulse,
        measurementSchema.enum.saturation,
        measurementSchema.enum.temperature,
        measurementSchema.enum.weight,
      ],
      startDate: machineFormat(startOfToday(), "yyyy-MM-dd"),
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    register,
    reset,
    setError,
    watch,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (newActivity: INewActivity) => addActivity(newActivity),
    onError: (error) => {
      setError("root.server", { message: deducedError(error) });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  // oxlint-disable-next-line react/incompatible-library
  const currentCategory = watch("category") as IActivityCategory | undefined;
  const activityHasPatient = patientId !== undefined;

  const {
    allowsHidden: activityIsHideable,
    allowsCompetence: currentCategoryAllowsCompetence,
    requiresCompetence: currentCategoryRequiresCompetence,
    allowsDuration: currentCategoryAllowsDuration,
    allowsDoubleStaffing: currentCategoryMayRequireDoubleStaffing,
  } = currentCategory
    ? getActivityCategoryConfig({
        category: currentCategory,
        activityHasPatient,
      })
    : {
        allowsHidden: false,
        allowsCompetence: false,
        requiresCompetence: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };

  const validateAndSubmit = handleSubmit((data) => {
    const {
      category,
      title,
      description,
      doubleStaffing,
      hidden,
      frequency,
      recurrence,
      requiredCompetences,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit,
      startDate,
      endDate,
      timeCategory,
      timeSensitivity,
      timeslots,
      weekdays,
      durationOption,
      customDuration,
      customDurationUnit,
      measurements,
    } = data;

    const duration = convertDurationToMinutes(
      durationOption,
      customDuration,
      customDurationUnit,
    );

    const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
    const isRecurring =
      frequency === activityFrequencySchema.enum.recurring &&
      recurrence !== null;
    const isRecurringAsInterval =
      recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

    const recurringTimeOfDayFields = isAnyTimeOfDay
      ? { isAnyTimeOfDay: true as const, timeslots: undefined }
      : {
          isAnyTimeOfDay: false as const,
          timeslots: timeslots as { time: string }[],
        };

    const timeFields = isRecurring
      ? {
          ...getTimeFields({
            isRecurringAsInterval,
            weekdays,
            recurrence,
            customRecurrenceInterval,
            customRecurrenceIntervalUnit,
            ...recurringTimeOfDayFields,
          }),
          endDate: dateInputSchema.decode(endDate) ?? undefined,
        }
      : {
          time: isAnyTimeOfDay
            ? ANY_TIME_OF_DAY
            : // @ts-expect-error we know there is exactly one timeslot when not recurring
              timeslots[0].time,
        };

    const newActivity = buildNewActivity({
      category,
      title,
      description,
      duration,
      hidden,
      startDate: calendarDateSchema.decode(startDate),
      timespan: getTimespan({ isAnyTimeOfDay, timeSensitivity }),
      patientId,
      requiredCompetences,
      doubleStaffing,
      measurements,
      timeFields,
    });

    mutate(newActivity);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Lägger till aktivitet`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Section>
              <ActivityCategoryPicker
                categoryConditionals={{
                  currentCategoryRequiresCompetence,
                  currentCategoryMayRequireDoubleStaffing,
                }}
                patientId={patientId}
              />
            </Form.Section>
            {currentCategory === categorySchema.enum.PatientMeasurementTask ? (
              <Form.Section>
                <MeasurementsPicker />
              </Form.Section>
            ) : null}
            <Form.Section width="half">
              <TitleInput />
              <DescriptionInput showHelperText />
            </Form.Section>
            <Form.Section>
              <Scheduling />
            </Form.Section>
            {showDuration && currentCategoryAllowsDuration ? (
              <Form.Section>
                <ActivityDuration />
              </Form.Section>
            ) : null}
            {currentCategoryAllowsCompetence ? (
              <Form.Section>
                <CompetencePicker
                  isRequired={currentCategoryRequiresCompetence}
                />
              </Form.Section>
            ) : null}
            {currentCategoryMayRequireDoubleStaffing || activityIsHideable ? (
              <Form.Section>
                <Checkboxes legend={t`Övrigt`} orientation="horizontal">
                  {currentCategoryMayRequireDoubleStaffing ? (
                    <Checkbox
                      errorMessage={errors.doubleStaffing?.message}
                      label={{ text: t`Kräver dubbelbemanning` }}
                      visualStyle="framed"
                      {...register("doubleStaffing")}
                    />
                  ) : (
                    <></>
                  )}
                  {activityIsHideable ? (
                    <Checkbox
                      errorMessage={errors.hidden?.message}
                      label={{ text: t`Dölj aktiviteten för patienten` }}
                      visualStyle="framed"
                      {...register("hidden")}
                    />
                  ) : (
                    <></>
                  )}
                </Checkboxes>
              </Form.Section>
            ) : null}
            <FilledButton type="submit" width="fill-container">
              <CheckmarkIcon />
              <Trans>Skapa aktiviteten</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
